import { useQuery } from "@tanstack/solid-query";
import { isServer } from "solid-js/web";
import { useWire } from "~/wire";
import { authTokenReady } from "~/wire/authTokenReady";
import type { PairedDevice } from "~/wire/services/DevicesService";

export const hasPairedDesktopDevice = (devices: PairedDevice[]): boolean =>
  devices.some((d) => !!d.hostname && !d.isArchived);

/**
 * Returns a getter that is `true` while it's still unknown whether the user
 * has a paired desktop device (query pending) or a paired device was found,
 * and `false` on error or when none is paired. Desktop-app promos should stay
 * hidden by default so pending state doesn't flash the nag at existing owners.
 */
export function useHasPairedDesktopDevice() {
  const wire = useWire();

  const query = useQuery(() => ({
    queryKey: ["chief-paired-devices"],
    enabled: !isServer && authTokenReady(wire.auth),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await wire.devices.listDevices();
      if (res.isErr()) throw res.error;
      return res.value;
    },
  }));

  return () => query.isPending || ((query.isSuccess || query.isRefetchError) && hasPairedDesktopDevice(query.data));
}
